/**
 * Composes provider connector UI for one paired workspace.
 *
 * Provider packages own their dialogs and credential state. This static
 * composition hook gives every provider the same add/edit overlay boundary;
 * runtime discovery would obscure which connectors are actually deployed.
 */
import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import type { WorkspaceVaultPairingSummary } from '../types'
import { listConnectorFeeds } from './api'
import { DisconnectConnectorDialog } from './components/DisconnectConnectorDialog'
import type { ConnectorFeed, ConnectorPickerAction } from './types'
import { connectorDefinitions } from './registry'

interface WorkspaceConnectorControlsOptions {
  api: NodeApiClient
  orgId: string
  vaultId: string
  pairing: WorkspaceVaultPairingSummary
  // True for workspace/global admins and for provider grant holders — anyone
  // the server admits to the connector configuration surface.
  canConfigure: boolean
  // Workspace authority owns encrypted credential storage and shared
  // presentation settings. Provider grants never imply this role.
  canAdministerWorkspace: boolean
  userId?: string | null
  refresh: () => Promise<void>
}

export function useWorkspaceConnectorControls({
  api,
  orgId,
  vaultId,
  pairing,
  canConfigure,
  canAdministerWorkspace,
  userId,
  refresh,
}: WorkspaceConnectorControlsOptions) {
  const queryClient = useQueryClient()
  const [editor, setEditor] = useState<{
    provider: ConnectorPickerAction['provider']
    feedId?: string
  }>()
  const [disconnectingFeed, setDisconnectingFeed] =
    useState<Pick<ConnectorFeed, 'feedId' | 'remoteFolderName'>>()
  const canManage = canConfigure
  const feeds = useQuery({
    queryKey: ['vault-connector-feeds', orgId, vaultId],
    queryFn: () => listConnectorFeeds(api, orgId, vaultId),
    enabled: canManage,
  })
  const canDefineQuery = feeds.data?.canDefineQueryByProvider ?? {}
  const queryAuthority = feeds.data?.queryAuthorityByProvider ?? {}
  const canResetWorkspace = feeds.data?.canResetWorkspaceByProvider ?? {}
  const sourceCount = (provider: ConnectorPickerAction['provider']) => (
    (feeds.data?.feeds ?? []).filter(
      feed => feed.provider === provider && feed.direction === 'inbound',
    ).length
  )
  const changed = async () => {
    // cm-coding: A saved connector dialog may close once the feed list reflects
    // its durable settings. Mark dependent cards and dashboards stale but let
    // them refetch in the background; one slow display query must not leave an
    // administrator trapped in an editor whose save already succeeded.
    void refresh()
    await queryClient.invalidateQueries({
      queryKey: ['vault-connector-feeds', orgId, vaultId],
    })
  }
  const showSyncProgress = useCallback(() => {
    setEditor(undefined)
    void refresh()
  }, [refresh])

  const editFeed = useCallback((feed: ConnectorFeed) => {
    setEditor({ provider: feed.provider, feedId: feed.feedId })
  }, [])
  // cm-coding: Every paired Vault should offer the complete registered connector
  // catalog. Let each provider dialog report missing credentials or query
  // authority; filtering the picker here would make available connector types
  // vary silently by application before the user can see how to enable them.
  const sourceActions: ConnectorPickerAction[] = connectorDefinitions().map(
    ([provider, definition]) => {
      const connectedCount = sourceCount(provider)
      const lacksQueryAuthority = !canDefineQuery[provider]
      return {
        id: `source-${provider}`,
        provider,
        label: definition.label,
        description: definition.sourceDescription,
        Icon: definition.SourceIcon,
        connectedCount,
        // Creating a feed defines its query, so the server-derived capability
        // gates the picker exactly like the create endpoint it leads to.
        disabled: connectedCount > 0 || lacksQueryAuthority,
        disabledReason: connectedCount === 0 && lacksQueryAuthority
          ? queryAuthority[provider] === 'workspace_admin'
            ? 'Requires workspace administrator access.'
            : `Requires a ${definition.label} connector administrator grant — its `
              + 'integration credentials read the full source data, so an '
              + 'organization admin must approve who defines its query.'
          : undefined,
        onSelect: () => setEditor({ provider }),
      }
    },
  )

  return {
    canManage,
    canDefineQuery,
    canResetWorkspace,
    queryAuthority,
    feeds: feeds.data?.feeds ?? [],
    sourcesReady: feeds.isSuccess,
    disconnectFeed: setDisconnectingFeed,
    editFeed,
    sourceActions,
    overlays: (
      <>
        {canManage && (
          <DisconnectConnectorDialog
            api={api}
            orgId={orgId}
            workspaceId={pairing.workspaceId}
            vaultId={vaultId}
            feed={disconnectingFeed}
            onOpenChange={open => { if (!open) setDisconnectingFeed(undefined) }}
            onComplete={changed}
          />
        )}
        {canManage && connectorDefinitions().map(([provider, definition]) => {
          const Editor = definition.Editor
          const active = editor?.provider === provider
          return (
            <Editor
              key={provider}
              api={api}
              orgId={orgId}
              workspaceId={pairing.workspaceId}
              vaultId={vaultId}
              feeds={feeds.data?.feeds ?? []}
              feed={active
                ? feeds.data?.feeds.find(feed => feed.feedId === editor.feedId)
                : undefined}
              userId={userId}
              canEditQuery={canDefineQuery[provider] ?? false}
              canAdministerWorkspace={canAdministerWorkspace}
              open={active}
              onOpenChange={(open) => setEditor(current => (
                open
                  ? current?.provider === provider ? current : { provider }
                  : undefined
              ))}
              onConfigured={changed}
              onSyncStarted={showSyncProgress}
            />
          )
        })}
      </>
    ),
  }
}
